import React, { useEffect, useMemo, useRef, useState } from 'react';
import Link from '@docusaurus/Link';
import { useHistory } from '@docusaurus/router';
import clsx from 'clsx';
import SearchBar from '@theme-original/SearchBar';
import rules from '@site/src/data/rules.json';
import styles from './styles.module.css';

function findDirectRule(query) {
  const match = query.trim().toLowerCase().match(/\bregras?\s*0?(\d{1,2})\b/);
  if (!match) {
    return null;
  }

  const numericId = Number.parseInt(match[1], 10);
  if (!Number.isInteger(numericId)) {
    return null;
  }

  const id = String(numericId).padStart(2, '0');
  return rules.find((rule) => rule.id === id) || null;
}

export default function SearchBarWrapper(props) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const blurTimerRef = useRef(null);
  const history = useHistory();
  const directRule = useMemo(() => findDirectRule(query), [query]);

  useEffect(() => {
    return () => {
      if (blurTimerRef.current) {
        window.clearTimeout(blurTimerRef.current);
      }
    };
  }, []);

  const handleInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.classList.contains('navbar__search-input')) {
      setQuery(event.target.value);
    }
  };

  const handleFocus = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.classList.contains('navbar__search-input')) {
      return;
    }

    if (blurTimerRef.current) {
      window.clearTimeout(blurTimerRef.current);
    }
    setFocused(true);
  };

  const handleBlur = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.classList.contains('navbar__search-input')) {
      return;
    }

    blurTimerRef.current = window.setTimeout(() => setFocused(false), 120);
  };

  const handleKeyDown = (event) => {
    if (
      event.key !== 'Enter' ||
      !directRule ||
      !(event.target instanceof HTMLInputElement) ||
      !event.target.classList.contains('navbar__search-input')
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    setFocused(false);
    event.target.blur();
    history.push(`/regras/#${directRule.id}`);
  };

  const showRuleShortcut = Boolean(directRule && focused);

  return (
    <div
      className={clsx(styles.searchBar, showRuleShortcut && styles.searchBarWithRuleShortcut)}
      onInput={handleInput}
      onFocusCapture={handleFocus}
      onBlurCapture={handleBlur}
      onKeyDownCapture={handleKeyDown}>
      <SearchBar {...props} />
      {showRuleShortcut && (
        <div className={styles.ruleShortcut} role="group" aria-label="Resultado direto de regra">
          <Link
            className={styles.ruleShortcutLink}
            to={`/regras/#${directRule.id}`}
            onPointerDown={() => {
              if (blurTimerRef.current) {
                window.clearTimeout(blurTimerRef.current);
              }
            }}
            onClick={() => setFocused(false)}>
            <span className={styles.ruleShortcutIcon} aria-hidden="true">
              #
            </span>
            <span className={styles.ruleShortcutContent}>
              <strong>Regra {directRule.id} — {directRule.title}</strong>
              <small>Regras · Ir diretamente para esta regra</small>
            </span>
            <span className={styles.ruleShortcutAction} aria-hidden="true">
              ↵
            </span>
          </Link>
        </div>
      )}
    </div>
  );
}
